<template>
    <div class="row" style="padding-bottom: 20px;">
        <div class="col-12 col-lg-8 col-xxl-9 d-flex">
            <div class="card flex-fill" id="map-card" ref="mapCard">
                <div class="card-header">
                    <div class="row">
                        <div class="col-12">
                            <h5 class="card-title">Carte | Tracer le parcours d'un device</h5>

                            <!-- Section de sélection du device -->
                            <div class="d-flex flex-wrap align-items-center gap-2 mb-3">

                                <!-- Dropdown de sélection du device -->
                                <div style="flex: 1; min-width: 200px;">
                                    <select v-model="selectedDeviceImei" class="form-control" :disabled="isLoading || isLiveMode || isIndividualTracking">
                                        <option value="">-- Sélectionner un device --</option>
                                        <option v-for="device in availableDevices" :key="device.imei"
                                            :value="device.imei">
                                            {{ device.display_name }} - {{ device.name }} ({{ device.imei }}) - {{ getTimeSinceLastConnection(device) }}
                                        </option>
                                    </select>
                                </div>

                                <!-- Date range picker (taille réduite) -->
                                <div style="flex: 0.5; min-width: 180px;">
                                    <div ref="reportRange"
                                        class="form-control d-flex justify-content-between align-items-center"
                                        :class="{ 'disabled': isLoading || !selectedDeviceImei || isLiveMode || isIndividualTracking }">
                                        <i class="far fa-calendar"></i>
                                        <span class="date-text">{{ searchDate.startDate || 'Période' }} {{ searchDate.endDate
                                            ? '- ' + searchDate.endDate : '' }}</span>
                                        <i class="fas fa-caret-down"></i>
                                    </div>
                                </div>

                                <!-- Boutons d'action -->
                                <div class="d-flex gap-2" v-if="!isLoading">
                                    <!-- Bouton Tracer -->
                                    <button class="btn btn-primary" @click="tracerDevice"
                                        :disabled="!selectedDeviceImei || isLiveMode || isIndividualTracking">
                                        <i class="fas fa-route"></i>
                                        Tracer
                                    </button>

                                    <!-- Bouton Suivi Individuel -->
                                    <button class="btn"
                                        :class="isIndividualTracking ? 'btn-warning' : 'btn-outline-success'"
                                        @click="isIndividualTracking ? stopIndividualTracking() : startIndividualTracking()"
                                        :disabled="isLiveMode"
                                        :title="isIndividualTracking ? 'Arrêter le suivi individuel' : 'Démarrer le suivi individuel'">
                                        <i class="fas" :class="isIndividualTracking ? 'fa-user-slash' : 'fa-user'"></i>
                                        {{ isIndividualTracking ? '⏸ Suivi' : '🎯 Suivi' }}
                                    </button>

                                    <!-- Bouton Temps Réel -->
                                    <button class="btn"
                                        :class="isLiveMode ? 'btn-success' : 'btn-outline-primary'"
                                        @click="isLiveMode ? stopLiveMode() : startLiveMode()"
                                        :disabled="isIndividualTracking"
                                        :title="isLiveMode ? 'Arrêter le temps réel' : 'Démarrer le temps réel'">
                                        <i class="fas" :class="isLiveMode ? 'fa-pause' : 'fa-play'"></i>
                                        {{ isLiveMode ? '⏸ Pause' : '▶️ Temps Réel' }}
                                    </button>

                                    <!-- Réinitialiser les dates -->
                                    <button v-if="searchDate.startDate" class="btn btn-outline-danger"
                                        @click="resetDate" title="Réinitialiser les dates">
                                        <i class="fas fa-times"></i>
                                    </button>

                                    <!-- Bouton Réinitialiser -->
                                    <button v-if="isTracing" class="btn btn-secondary" @click="resetTracing">
                                        <i class="fas fa-undo"></i>
                                        Réinitialiser
                                    </button>
                                </div>

                                <!-- Loader -->
                                <div v-if="isLoading" class="d-flex align-items-center">
                                    <span class="loader"></span>
                                    <span class="ml-2">Chargement...</span>
                                </div>
                            </div>

                            <!-- Indicateur de statut temps réel -->
                            <div v-if="isLiveMode" class="alert alert-info mb-2 d-flex align-items-center justify-content-between">
                                <div class="d-flex align-items-center gap-2">
                                    <span class="badge bg-success pulse-badge">
                                        <i class="fas fa-circle pulse-animation"></i>
                                        LIVE
                                    </span>
                                    <span>
                                        <strong>Mise à jour toutes les {{ currentPollingRate / 1000 }}s</strong>
                                        <span v-if="currentPollingRate === FAST_POLLING_RATE" class="text-success ms-2">
                                            (Mode rapide - Mouvement détecté)
                                        </span>
                                        <span v-else class="text-muted ms-2">
                                            (Mode normal)
                                        </span>
                                    </span>
                                </div>
                                <div v-if="movementStatus.active" class="badge bg-info">
                                    {{ movementStatus.message }}
                                </div>
                            </div>

                            <!-- Outils de carte (GeoJSON + Mesure de distance + Points d'Intérêt + Satellite + Plein écran) -->
                            <div class="mt-2 d-flex flex-wrap gap-2 align-items-center">
                                <!-- <GeoJsonLayerControl v-if="map" :map="map" /> -->

                                <!-- Composant de mesure de distance -->
                                <DistanceMeasureTool
                                    v-if="map"
                                    :map="map"
                                    ref="distanceTool"
                                    @measure-mode-changed="onMeasureModeChanged"
                                />

                                <!-- Points d'Intérêt (compact) -->
                                <ManualMarker
                                    v-if="map"
                                    :map="map"
                                    @show-toast="showToast"
                                />

                                <!-- Vue Satellite -->
                                <SatelliteToggle v-if="map" :map="map" />

                                <!-- Bouton plein écran -->
                                <FullscreenMapButton
                                    v-if="map"
                                    target-element="map-card"
                                    @fullscreen-changed="onFullscreenChanged"
                                />
                            </div>

                            <!-- Info du device tracé -->
                            <div v-if="isTracing && tracedDeviceInfo" class="alert alert-info mb-0 mt-2">
                                <strong>📍 Parcours tracé :</strong> {{ tracedDeviceInfo.name }} ({{
                                    tracedDeviceInfo.display_name }})
                                - <strong>{{ tracedPoints }} points</strong>
                                <span v-if="totalDistance > 0" class="ml-2">
                                    - <strong>📏 Distance : {{ formatDistance(totalDistance) }}</strong>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Carte -->
                <div :style="mapContainerStyle" id="test"></div>
            </div>
        </div>

        <!-- Information -->
        <InformationContent />

        <!-- Modal de configuration du suivi individuel -->
        <div v-if="showTrackingModal" class="modal-overlay" @click="closeTrackingModal">
            <div class="modal-content tracking-modal" @click.stop>
                <div class="modal-header">
                    <h5>🎯 Suivi GPS Individuel</h5>
                    <button type="button" class="btn-close" @click="closeTrackingModal"></button>
                </div>
                <div class="modal-body">
                    <!-- Sélection du device -->
                    <div class="mb-3">
                        <label class="form-label">Device à suivre</label>
                        <select v-model="trackingDeviceImei" class="form-select">
                            <option value="">-- Sélectionner un device --</option>
                            <option v-for="device in availableDevices" :key="device.imei"
                                :value="device.imei">
                                {{ device.display_name }} - {{ device.name }} ({{ device.imei }})
                            </option>
                        </select>
                    </div>

                    <!-- Informations du device sélectionné -->
                    <div v-if="trackingDeviceImei" class="device-info mb-3">
                        <div class="device-info-box">
                            <strong>📍 Informations :</strong><br>
                            <span id="device-last-position">Chargement...</span>
                        </div>
                    </div>

                    <!-- Options de suivi -->
                    <div class="mb-3">
                        <label class="form-label">Options de suivi</label>
                        <div class="form-check">
                            <input class="form-check-input" type="checkbox" v-model="trackingOptions.centerMap" id="centerMap">
                            <label class="form-check-label" for="centerMap">
                                🎯 Centrer automatiquement la carte sur le device
                            </label>
                        </div>
                        <div class="form-check">
                            <input class="form-check-input" type="checkbox" v-model="trackingOptions.showPath" id="showPath">
                            <label class="form-check-label" for="showPath">
                                ️ Afficher le tracé du parcours
                            </label>
                        </div>
                        <div class="form-check">
                            <input class="form-check-input" type="checkbox" v-model="trackingOptions.showStats" id="showStats">
                            <label class="form-check-label" for="showStats">
                                📊 Afficher les statistiques en temps réel
                            </label>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="closeTrackingModal">Annuler</button>
                    <button
                        type="button"
                        class="btn btn-success"
                        @click="confirmStartTracking"
                        :disabled="!trackingDeviceImei"
                    >
                        <i class="fas fa-play"></i>
                        ▶️ Démarrer le suivi
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
.pind {
    position: absolute !important;
    background-color: transparent !important;
    border: none;
    color: #fff;
}

.pindPoint {
    background-color: transparent !important;
    border: none !important;
    border-radius: 60px !important;
    width: 2px;
}

.pin {
    position: absolute;
    color: #fff;
    margin: -30px 0px 0px -10px;
    border-radius: 50% 50% 50% 0;
    background-color: red;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 30px;
    height: 28px;
    transform: rotate(-45deg);
}

.gap-2 {
    gap: 0.5rem;
}

.disabled {
    opacity: 0.6;
    pointer-events: none;
}

/* Styles pour le plein écran */
#map-card:fullscreen {
    width: 100vw !important;
    height: 100vh !important;
    max-width: 100vw !important;
}

#map-card:fullscreen .card {
    height: 100vh !important;
}

/* Pour Safari */
#map-card:-webkit-full-screen {
    width: 100vw !important;
    height: 100vh !important;
}

/* Animations pour le mode temps réel */
@keyframes pulse {
    0%, 100% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.7;
        transform: scale(1.1);
    }
}

.pulse-animation {
    animation: pulse 2s ease-in-out infinite;
}

.pulse-badge {
    animation: pulse 1.5s ease-in-out infinite;
}

.ml-2 {
    margin-left: 8px;
}

/* Style pour le texte des dates - version compacte */
.date-text {
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 80px;
}

/* Modal de tracking */
.modal-overlay {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    background-color: rgba(0, 0, 0, 0.5) !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    z-index: 9998 !important;
}

.modal-content {
    background-color: white !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
    max-width: 90vw !important;
    max-height: 90vh !important;
    overflow-y: auto !important;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    border-bottom: 1px solid #dee2e6;
}

.modal-body {
    padding: 20px;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 15px 20px;
    border-top: 1px solid #dee2e6;
}

.tracking-modal {
    max-width: 600px;
}

.device-info {
    font-size: 12px;
}
</style>


<style>
/* Styles globaux pour le modal de tracking (non-scoped) */
.modal-overlay {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    background-color: rgba(0, 0, 0, 0.5) !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    z-index: 9999 !important;
}

.modal-content {
    background-color: white !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
    max-width: 600px !important;
    width: 90% !important;
    max-height: 90vh !important;
    overflow-y: auto !important;
}

.modal-header {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    padding: 15px 20px !important;
    border-bottom: 1px solid #dee2e6 !important;
}

.modal-body {
    padding: 20px !important;
}

.modal-footer {
    display: flex !important;
    justify-content: flex-end !important;
    gap: 10px !important;
    padding: 15px 20px !important;
    border-top: 1px solid #dee2e6 !important;
}
</style>

<script>
import { defineComponent } from 'vue';
import { mapFields } from 'vuex-map-fields';
import 'jquery';
import 'moment';
import fetch from '@/utils/fetch';
import 'daterangepicker';
import InformationContent from "../components/informations/InformationContent.vue";
import store from '@/store';
import * as types from '@/store/modules/map/list/mutation_types';
import { mapActions } from 'vuex';
import leaflet from 'leaflet';
// import GeoJsonLayerControl from "../components/map/GeoJsonLayerControl.vue";
import DistanceMeasureTool from "../components/map/DistanceMeasureTool.vue";
import FullscreenMapButton from "../components/map/FullscreenMapButton.vue";
import ManualMarker from "../components/markers/ManualMarker.vue";
import SatelliteToggle from "../components/map/SatelliteToggle.vue";

export default defineComponent({
    components: {
        InformationContent,
        // GeoJsonLayerControl,
        DistanceMeasureTool,
        FullscreenMapButton,
        ManualMarker,
        SatelliteToggle,
    },

    data() {
        return {
            map: null,
            markers: [],
            selectedDeviceImei: '',
            isTracing: false,
            tracedDeviceInfo: null,
            tracedPoints: 0,
            totalDistance: 0, // Distance totale en mètres
            allDevicesData: [],
            isMeasureMode: false,
            isFullscreen: false,

            // Live mode - Temps réel
            isLiveMode: false,
            pollingInterval: null,
            currentPollingRate: 30000,
            lastPositions: {},
            movementStatus: {
                active: false,
                message: '',
                count: 0,
                timestamp: null
            },
            cyclesWithoutMovement: 0,

            // Suivi individuel
            isIndividualTracking: false,
            showTrackingModal: false,
            trackingDeviceImei: null,
            trackingOptions: {
                centerMap: true,
                showPath: true,
                showStats: true
            },
            individualTrackingInterval: null,
            trackingPath: [], // Polyline du tracé
            trackingStats: {
                distance: 0,
                startTime: null,
                lastUpdate: null,
                lastPosition: null
            },

            // Configuration
            MOVEMENT_THRESHOLD: 10, // 10 mètres
            FAST_POLLING_RATE: 5000, // 5 secondes
            NORMAL_POLLING_RATE: 30000, // 30 secondes
            FAST_MODE_CYCLES_BEFORE_NORMAL: 24, // 24 cycles = 2 minutes
        };
    },

    computed: {
        ...mapFields('map/list', {
            items: 'items',
            center: 'centerMap',
            zoom: 'zoom',
            searchDate: 'searchDate',
            isLoading: 'isLoading',
        }),

        mapContainerStyle() {
            return {
                height: this.isFullscreen ? '100vh' : '500px',
                width: '100%'
            };
        },

        availableDevices() {
            if (!this.allDevicesData || this.allDevicesData.length === 0) {
                return [];
            }

            return this.allDevicesData.filter(device =>
                device && device.imei
            );
        },

        filteredDevicesForTracking() {
            if (!this.deviceSearchQuery || this.deviceSearchQuery.trim() === '') {
                return this.availableDevices;
            }

            const query = this.deviceSearchQuery.toLowerCase().trim();
            return this.availableDevices.filter(device => {
                const name = (device.name || '').toLowerCase();
                const displayName = (device.display_name || '').toLowerCase();
                const imei = (device.imei || '').toLowerCase();
                return name.includes(query) || displayName.includes(query) || imei.includes(query);
            });
        }
    },

    watch: {
        items() {
            if (this.isTracing) {
                this.displayTracedRoute();
            } else {
                this.removeOldMarker();
                this.markerTracer();
            }
        },

        center() {
            if (this.center && !isNaN(this.center.lng) && !isNaN(this.center.lat)) {
                this.map.setView(this.center, this.zoom);
            }
        },

        selectedDeviceImei(newImei) {
            if (newImei) {
                const device = this.availableDevices.find(d => d.imei === newImei);
                if (device) {
                    this.tracedDeviceInfo = device;
                }
            }
        },

        trackingDeviceImei(newImei) {
            if (newImei) {
                // Mettre à jour les infos du device dans le modal
                this.$nextTick(() => {
                    this.updateDeviceInfo();
                });
            }
        }
    },

    mounted() {
        this.initDataRanger();
        this.initStreetMap();
        this.loadAllDevices();
    },

    unmounted() {
        this.resetMapData();
        // Nettoyer le timer de polling
        this.stopLiveMode();
        // Nettoyer le suivi individuel
        this.stopIndividualTracking();
    },

    methods: {
        ...mapActions({
            getMapData: "map/list/getMapData",
            resetMapData: "map/list/resetMapData",
            centerMapToMarker: "map/list/centerPosition"
        }),
        
        initStreetMap() {
            this.map = leaflet.map("test");
            this.map.setView([9.5704107, 3.0290977], 10);

            leaflet.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            }).addTo(this.map);

            // Attendre que la carte soit prête puis ajuster le zoom
            this.map.whenReady(() => {
                this.fitMapToDevices();
            });
        },

        /**
         * Ajuste automatiquement le zoom pour montrer tous les devices
         */
        fitMapToDevices() {
            if (this.items && this.items.length > 0 && this.map) {
                const bounds = [];
                this.items.forEach((item) => {
                    if (item && item.point_latitude && item.point_longitude) {
                        bounds.push([
                            parseFloat(item.point_latitude),
                            parseFloat(item.point_longitude)
                        ]);
                    }
                });

                if (bounds.length > 0) {
                    // Ajuster la vue pour montrer tous les devices avec du padding
                    this.map.fitBounds(bounds, {
                        padding: [50, 50],
                        maxZoom: 14  // Ne pas zoomer plus de 14 pour garder du contexte
                    });
                }
            }
        },

        async loadAllDevices() {
            try {
                const devicesResponse = await fetch({ method: 'GET', url: 'devices?lastevent=true' });
                this.allDevicesData = devicesResponse.data.data;
            } catch (error) {
                console.error('Erreur lors du chargement des devices:', error);
            }
        },

        async tracerDevice() {
            if (!this.selectedDeviceImei) {
                alert('Veuillez sélectionner un device');
                return;
            }

            this.isTracing = true;

            let params = `?id[eq]=${this.selectedDeviceImei}&lastevent=false`;

            if (this.searchDate.startDate && this.searchDate.endDate) {
                params += `&start_date=${this.searchDate.startDate}&end_date=${this.searchDate.endDate}`;
            }

            await this.getMapData({
                type: params,
                data: null,
                isParamsFilter: false
            });
        },

        displayTracedRoute() {
            this.removeOldMarker();

            if (!this.items || this.items.length === 0) {
                alert('Aucune donnée trouvée pour ce device');
                this.isTracing = false;
                return;
            }

            const validEvents = this.items.filter(e => e && e.point_latitude && e.point_longitude);

            if (validEvents.length === 0) {
                alert('Aucune position GPS valide trouvée');
                this.isTracing = false;
                return;
            }

            this.tracedPoints = validEvents.length;

            const points = validEvents.map(event => [
                parseFloat(event.point_latitude),
                parseFloat(event.point_longitude)
            ]);

            // Calculer la distance totale du parcours
            this.totalDistance = this.calculateTotalDistance(points);

            const color = '#FF0000';

            const polyline = leaflet.polyline(points, {
                color: color,
                weight: 3,
                opacity: 0.8
            }).addTo(this.map);

            this.markers.push(polyline);

            const startIcon = leaflet.divIcon({
                className: "pind leaflet-div-icon",
                html: `<div class="pin" style="background-color:${color}">
                    <div style="transform:rotate(45deg);font-size:10px">Début</div>
                </div>`
            });

            const endIcon = leaflet.divIcon({
                className: "pind leaflet-div-icon",
                html: `<div class="pin" style="background-color:${color}">
                    <div style="transform:rotate(45deg);font-size:10px">Fin</div>
                </div>`
            });

            const startMarker = leaflet.marker(points[0], { icon: startIcon }).addTo(this.map);
            const endMarker = leaflet.marker(points[points.length - 1], { icon: endIcon }).addTo(this.map);

            this.markers.push(startMarker, endMarker);

            points.forEach((point, index) => {
                if (index > 0 && index < points.length - 1 && index % 5 === 0) {
                    const pointIcon = leaflet.divIcon({
                        className: "pindPoint leaflet-div-icon",
                        html: `<div style="background-color:${color};min-width:100%;min-height:100%;border-radius:60px"></div>`
                    });

                    const marker = leaflet.marker(point, { icon: pointIcon }).addTo(this.map);
                    this.markers.push(marker);
                }
            });

            this.map.fitBounds(polyline.getBounds(), { padding: [50, 50] });
        },

        async resetTracing() {
            this.isTracing = false;
            this.selectedDeviceImei = '';
            this.tracedDeviceInfo = null;
            this.tracedPoints = 0;
            this.totalDistance = 0;
            this.resetDate();
            await this.loadAllDevices();
        },

        markerTracer() {
            const markers = [];

            this.items.forEach((item) => {
                if (item && item.point_latitude && item.point_longitude) {
                    const icon = leaflet.divIcon({
                        className: "pind leaflet-div-icon",
                        html: `<div class="pin" style="background-color:${item.color}">
                            <div style="transform:rotate(45deg)">${item.display_name}</div>
                        </div>`
                    });

                    const marker = leaflet.marker(
                        [parseFloat(item.point_latitude), parseFloat(item.point_longitude)],
                        { icon }
                    );

                    // Stocker les infos du device avec le marqueur
                    marker.deviceImei = item.imei;
                    marker.deviceName = item.name;
                    marker.deviceColor = item.color;

                    marker.on("click", () => {
                        if (this.isMeasureMode && this.$refs.distanceTool) {
                            this.$refs.distanceTool.onMarkerClick(
                                marker.getLatLng(),
                                {
                                    display_name: item.display_name,
                                    name: item.name,
                                    imei: item.imei
                                }
                            );
                        } else {
                            this.selectedDeviceImei = item.imei;
                            this.setMapViewToMarker(item, marker.getLatLng(), 10);

                            // Copier les coordonnées dans le presse-papiers
                            const coords = `${item.point_latitude}, ${item.point_longitude}`;

                            // Utiliser l'ancienne méthode pour plus de compatibilité
                            const textArea = document.createElement('textarea');
                            textArea.value = coords;
                            textArea.style.position = 'fixed';
                            textArea.style.left = '-999999px';
                            document.body.appendChild(textArea);
                            textArea.select();

                            try {
                                document.execCommand('copy');
                                this.showToast(`📋 Coordonnées copiées : ${coords}`);
                            } catch (err) {
                                console.error('Erreur lors de la copie:', err);
                                // Fallback: afficher les coordonnées sans copier
                                this.showToast(`📍 Coordonnées : ${coords}`);
                            } finally {
                                document.body.removeChild(textArea);
                            }
                        }
                    });

                    marker.addTo(this.map);
                    markers.push(marker);
                }
            });

            this.markers = markers;
        },

        removeOldMarker() {
            if (this.markers.length) {
                this.markers.forEach(marker => {
                    marker.remove();
                });
                this.markers = [];
            }
        },

        setMapViewToMarker(selectItem, position, zoom = 10) {
            this.centerMapToMarker({ position, zoom, selectItem });
        },

        resetDate() {
            store.commit(`map/list/${types.SET_SEARCH_DATE}`, {
                startDate: null,
                endDate: null
            });
        },

        initDataRanger() {
            const reportRange = this.$refs.reportRange;

            $(reportRange).daterangepicker({
                opens: "left",
                autoUpdateInput: false,
                locale: {
                    cancelLabel: 'Effacer',
                    applyLabel: 'Appliquer'
                },
                ranges: {
                    "Aujourd'hui": [moment(), moment()],
                    "Hier": [moment().subtract(1, "days"), moment().subtract(1, "days")],
                    "7 derniers jours": [moment().subtract(6, "days"), moment()],
                    "30 derniers jours": [moment().subtract(29, "days"), moment()],
                    "Ce mois-ci": [moment().startOf("month"), moment().endOf("month")],
                    "Mois dernier": [
                        moment().subtract(1, "month").startOf("month"),
                        moment().subtract(1, "month").endOf("month")
                    ]
                }
            }, (start, end) => {
                store.commit(`map/list/${types.SET_SEARCH_DATE}`, {
                    startDate: start.format("YYYY-MM-DD"),
                    endDate: end.format("YYYY-MM-DD")
                });
            });

            $(reportRange).on('cancel.daterangepicker', () => {
                this.resetDate();
            });
        },

        showToast(message) {
            const toast = document.createElement('div')
            toast.textContent = message
            toast.style.position = 'fixed'
            toast.style.bottom = '30px'
            toast.style.right = '30px'
            toast.style.padding = '12px 18px'
            toast.style.background = 'linear-gradient(135deg, #4CAF50, #2E7D32)'
            toast.style.color = 'white'
            toast.style.borderRadius = '10px'
            toast.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)'
            toast.style.fontFamily = 'system-ui, sans-serif'
            toast.style.fontSize = '15px'
            toast.style.zIndex = '9999'
            toast.style.opacity = '0'
            toast.style.transition = 'opacity 0.3s ease, transform 0.3s ease'
            toast.style.transform = 'translateY(20px)'

            document.body.appendChild(toast)

            setTimeout(() => {
                toast.style.opacity = '1'
                toast.style.transform = 'translateY(0)'
            }, 50)

            setTimeout(() => {
                toast.style.opacity = '0'
                toast.style.transform = 'translateY(20px)'
                setTimeout(() => toast.remove(), 300)
            }, 3000)
        },

        onMeasureModeChanged(isMeasuring) {
            this.isMeasureMode = isMeasuring;
            
            if (isMeasuring) {
                this.map.getContainer().style.cursor = 'crosshair';
            } else {
                this.map.getContainer().style.cursor = '';
            }
        },

        onFullscreenChanged(isFullscreen) {
            this.isFullscreen = isFullscreen;

            setTimeout(() => {
                if (this.map) {
                    this.map.invalidateSize();
                    // Ajuster le zoom pour bien voir les devices en plein écran
                    this.fitMapToDevices();
                }
            }, 100);
        },

        // ==================== LIVE MODE - TEMPS RÉEL ====================

        /**
         * Calcule la distance en mètres entre deux coordonnées GPS
         * Utilise la formule de Haversine
         */
        calculateDistance(lat1, lon1, lat2, lon2) {
            const R = 6371e3; // Rayon de la Terre en mètres
            const φ1 = lat1 * Math.PI / 180;
            const φ2 = lat2 * Math.PI / 180;
            const Δφ = (lat2 - lat1) * Math.PI / 180;
            const Δλ = (lon2 - lon1) * Math.PI / 180;

            const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
                      Math.cos(φ1) * Math.cos(φ2) *
                      Math.sin(Δλ/2) * Math.sin(Δλ/2);

            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

            return R * c; // Distance en mètres
        },

        /**
         * Calcule la distance totale d'un parcours GPS
         * @param {Array} points - Array de points [[lat, lng], [lat, lng], ...]
         * @returns {Number} - Distance totale en mètres
         */
        calculateTotalDistance(points) {
            if (!points || points.length < 2) {
                return 0;
            }

            let totalDistance = 0;

            // Utiliser la méthode native de Leaflet pour le calcul de distance
            for (let i = 1; i < points.length; i++) {
                const latlng1 = leaflet.latLng(points[i - 1][0], points[i - 1][1]);
                const latlng2 = leaflet.latLng(points[i][0], points[i][1]);
                totalDistance += this.map.distance(latlng1, latlng2);
            }

            return totalDistance; // Distance en mètres
        },

        /**
         * Formate une distance en mètres pour l'affichage
         * @param {Number} meters - Distance en mètres
         * @returns {String} - Distance formatée (ex: "1.5 km" ou "500 m")
         */
        formatDistance(meters) {
            if (meters < 1000) {
                return `${Math.round(meters)} m`;
            } else {
                return `${(meters / 1000).toFixed(2)} km`;
            }
        },

        /**
         * Démarre le mode temps réel
         */
        startLiveMode() {
            if (this.isLiveMode) return;

            this.isLiveMode = true;
            this.cyclesWithoutMovement = 0;
            this.currentPollingRate = this.NORMAL_POLLING_RATE;

            // Premier polling immédiat
            this.pollDevices();

            // Démarrer le timer
            this.pollingInterval = setInterval(() => {
                this.pollDevices();
            }, this.currentPollingRate);

            this.showToast('▶️ Mode temps réel activé - Mise à jour toutes les 30s');
        },

        /**
         * Arrête le mode temps réel
         */
        stopLiveMode() {
            if (!this.isLiveMode) return;

            this.isLiveMode = false;

            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
            }

            // Reset du statut de mouvement
            this.movementStatus = {
                active: false,
                message: '',
                count: 0,
                timestamp: null
            };

            this.showToast('⏸ Mode temps réel désactivé');
        },

        // ==================== SUIVI INDIVIDUEL ====================

        startIndividualTracking() {
            if (!this.selectedDeviceImei) {
                // Ouvrir le modal de configuration
                this.showTrackingModal = true;
                return;
            }

            this.startTrackingForDevice(this.selectedDeviceImei);
        },

        startTrackingForDevice(imei) {
            this.isIndividualTracking = true;
            this.trackingDeviceImei = imei;
            this.trackingStats.startTime = new Date();
            this.trackingPath = [];
            this.trackingStats.distance = 0;

            // Nettoyer la carte
            this.removeOldMarker();

            // Fermer le modal
            this.showTrackingModal = false;

            // Charger immédiatement les positions
            this.loadDeviceHistoryForTracking(imei);
        },

        async loadDeviceHistoryForTracking(imei) {
            try {
                // Récupérer les 10 dernières positions directement via le paramètre limit
                let params = `?id[eq]=${imei}&lastevent=false&limit=10`;

                const response = await this.getMapData({
                    type: params,
                    data: null,
                    isParamsFilter: false
                });

                // Extraire eventsdata de la réponse de l'API
                const events = response?.data?.[0]?.eventsdata || [];

                if (!events || events.length === 0) {
                    this.showToast('⚠️ Aucune position historique trouvée');
                    // Afficher seulement la position actuelle
                    await this.showCurrentDevicePosition(imei);
                    // Démarrer le polling
                    this.startIndividualPolling();
                    return;
                }

                // Prendre les 10 dernières positions
                const recentEvents = events.slice(-10).reverse(); // Plus récentes d'abord
                const positions = recentEvents.map(event => [
                    parseFloat(event.point_latitude),
                    parseFloat(event.point_longitude)
                ]);

                // Afficher le tracé historique
                if (positions.length > 1) {
                    this.trackingPath = positions;

                    const historyPolyline = leaflet.polyline(positions, {
                        color: '#4A90E2', // Bleu pour l'historique
                        weight: 2,
                        opacity: 0.5
                    }).addTo(this.map);

                    this.markers.push(historyPolyline);
                }

                // Afficher la position actuelle
                await this.showCurrentDevicePosition(imei);

                // Centrer la carte pour montrer tout le tracé
                if (positions.length > 1) {
                    const bounds = leaflet.latLngBounds(positions);
                    this.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
                }

                this.showToast(`✅ Suivi actif: ${recentEvents.length} positions chargées`);

                // Démarrer le polling
                this.startIndividualPolling();

            } catch (error) {
                console.error('Erreur lors du chargement de l\'historique:', error);
                this.showToast('❌ Erreur lors du chargement des positions');
                this.stopIndividualTracking();
            }
        },

        startIndividualPolling() {
            // Premier polling immédiat
            this.pollIndividualDevice();

            // Puis polling toutes les 5 secondes
            this.individualTrackingInterval = setInterval(() => {
                this.pollIndividualDevice();
            }, 5000);
        },

        async showCurrentDevicePosition(imei) {
            try {
                // Récupérer tous les devices pour avoir la position actuelle
                const response = await fetch({
                    method: 'GET',
                    url: 'devices?lastevent=true'
                });

                const devices = response.data.data;

                if (!devices || devices.length === 0) {
                    this.showToast('⚠️ Aucun device trouvé');
                    return;
                }

                const device = devices.find(d => d.imei === imei);
                if (!device || !device.latest_event) {
                    this.showToast('⚠️ Device sans position récente');
                    return;
                }

                // Afficher immédiatement la position actuelle
                this.updateIndividualTrackingDisplay(device);

            } catch (error) {
                console.error('Erreur lors de l\'affichage de la position:', error);
            }
        },

        stopIndividualTracking() {
            if (!this.isIndividualTracking) return;

            this.isIndividualTracking = false;

            if (this.individualTrackingInterval) {
                clearInterval(this.individualTrackingInterval);
                this.individualTrackingInterval = null;
            }

            // Nettoyer les marqueurs et tracé
            this.removeOldMarker();

            // Réinitialiser les stats
            this.trackingPath = [];
            this.trackingStats = {
                distance: 0,
                startTime: null,
                lastUpdate: null,
                lastPosition: null
            };

            this.showToast('⏸ Suivi individuel arrêté');
        },

        getDeviceName(imei) {
            const device = this.availableDevices.find(d => d.imei === imei);
            return device ? device.name : imei;
        },

        closeTrackingModal() {
            this.showTrackingModal = false;
            this.trackingDeviceImei = null;
        },

        updateDeviceInfo() {
            if (!this.trackingDeviceImei) return;

            const device = this.availableDevices.find(d => d.imei === this.trackingDeviceImei);
            if (!device) return;

            const lastPosElement = document.getElementById('device-last-position');
            if (lastPosElement && device.latest_event) {
                lastPosElement.innerHTML = `
                    📍 Position : ${parseFloat(device.latest_event.point_latitude).toFixed(6)}, ${parseFloat(device.latest_event.point_longitude).toFixed(6)}<br>
                    🕐 Dernière mise à jour : ${this.getTimeSinceLastConnection(device)}<br>
                    🔋 Batterie : ${device.latest_event.battery_level || 'N/A'}
                `;
            }
        },

        confirmStartTracking() {
            if (!this.trackingDeviceImei) {
                alert('Veuillez sélectionner un device');
                return;
            }
            this.startTrackingForDevice(this.trackingDeviceImei);
        },

        async pollIndividualDevice() {
            try {
                // Récupérer les dernières positions depuis l'API
                const response = await fetch({
                    method: 'GET',
                    url: `devices?lastevent=true`
                });

                const devices = response.data.data;

                if (!devices || devices.length === 0) {
                    console.log('Aucun device trouvé');
                    return;
                }

                // Trouver le device qu'on suit
                const device = devices.find(d => d.imei === this.trackingDeviceImei);
                if (!device) {
                    console.log('Device suivi non trouvé');
                    return;
                }
                const currentPos = {
                    lat: parseFloat(device.latest_event.point_latitude),
                    lng: parseFloat(device.latest_event.point_longitude),
                    timestamp: device.latest_event.created_at
                };

                // Calculer la distance si on a une position précédente
                if (this.trackingStats.lastPosition) {
                    const distance = this.calculateDistance(
                        this.trackingStats.lastPosition.lat,
                        this.trackingStats.lastPosition.lng,
                        currentPos.lat,
                        currentPos.lng
                    );

                    if (distance > 5) { // Plus de 5 mètres = mouvement significatif
                        this.trackingStats.distance += distance;

                        // Ajouter au tracé si option activée
                        if (this.trackingOptions.showPath) {
                            this.trackingPath.push([currentPos.lat, currentPos.lng]);
                            this.updateTrackingPath();
                        }
                    }
                }

                // Mettre à jour la position
                this.trackingStats.lastPosition = currentPos;
                this.trackingStats.lastUpdate = new Date();

                // Mettre à jour l'affichage sur la carte
                this.updateIndividualTrackingDisplay(device);

            } catch (error) {
                console.error('Erreur lors du polling individuel:', error);
            }
        },

        updateIndividualTrackingDisplay(device) {
            // Afficher le marker du device (garder le tracé existant)
            const oldMarkers = this.markers.filter(m => m instanceof leaflet.Marker);
            oldMarkers.forEach(m => m.remove());

            const icon = leaflet.divIcon({
                className: "pind leaflet-div-icon",
                html: `<div class="pin" style="background-color:${device.color}; transform: scale(1.3);">
                    <div style="transform:rotate(45deg)">${device.display_name}</div>
                </div>`
            });

            const marker = leaflet.marker(
                [parseFloat(device.latest_event.point_latitude), parseFloat(device.latest_event.point_longitude)],
                { icon }
            );

            marker.addTo(this.map);
            this.markers.push(marker);

            // Centrer la carte si option activée (sans changer le zoom)
            if (this.trackingOptions.centerMap) {
                this.map.panTo(
                    [parseFloat(device.latest_event.point_latitude), parseFloat(device.latest_event.point_longitude)]
                );
            }
        },

        updateTrackingPath() {
            // Afficher le tracé si on a des points
            if (this.trackingPath.length < 2) return;

            // Retirer l'ancien tracé s'il existe
            const oldPathIndex = this.markers.findIndex(m => m instanceof leaflet.Polyline);
            if (oldPathIndex !== -1) {
                this.markers[oldPathIndex].remove();
                this.markers.splice(oldPathIndex, 1);
            }

            // Créer le nouveau tracé
            const polyline = leaflet.polyline(this.trackingPath, {
                color: '#FF0000',
                weight: 3,
                opacity: 0.7
            }).addTo(this.map);

            this.markers.push(polyline);
        },

        /**
         * Récupère les nouvelles positions et détecte les mouvements
         */
        async pollDevices() {
            try {
                // Récupérer les dernières positions depuis l'API
                const response = await fetch({
                    method: 'GET',
                    url: 'devices?lastevent=true'
                });

                const newDevices = response.data.data;

                if (!newDevices || newDevices.length === 0) {
                    console.log('Aucun device trouvé');
                    return;
                }

                // Détecter les mouvements
                let movementDetected = false;
                const movingDevices = [];

                newDevices.forEach(device => {
                    if (!device.latest_event) return;

                    const imei = device.imei;
                    const currentPos = {
                        lat: parseFloat(device.latest_event.point_latitude),
                        lng: parseFloat(device.latest_event.point_longitude),
                        timestamp: device.latest_event.created_at,
                        speed: device.latest_event.speed || 0
                    };

                    // Récupérer l'ancienne position
                    const oldPos = this.lastPositions[imei];

                    // Calculer la distance
                    let distance = 0;

                    if (oldPos) {
                        distance = this.calculateDistance(
                            oldPos.lat, oldPos.lng,
                            currentPos.lat, currentPos.lng
                        );

                        // Déterminer si le device a bougé
                        if (distance > this.MOVEMENT_THRESHOLD) {
                            movementDetected = true;
                            movingDevices.push({
                                imei: imei,
                                name: device.name,
                                distance: distance,
                                speed: currentPos.speed
                            });

                            console.log(`🚗 Device ${device.name} a bougé de ${distance.toFixed(1)}m`);
                        }
                    }

                    // Mettre à jour la position stockée
                    this.lastPositions[imei] = currentPos;
                });

                // Ajuster la fréquence de polling
                this.adjustPollingRate(movementDetected);

                // Mettre à jour la carte via Vuex
                store.commit(`map/list/${types.SET_ITEMS}`, this.extractEvents(newDevices));

                // Ajuster le zoom pour bien voir tous les devices
                setTimeout(() => {
                    this.fitMapToDevices();
                }, 200);

                // Afficher les infos de mouvement
                if (movingDevices.length > 0) {
                    this.showMovementNotificationTop(movingDevices);
                }

            } catch (error) {
                console.error('Erreur lors du polling:', error);
            }
        },

        /**
         * Extrait les événements des devices (même logique que fetchEvents)
         */
        extractEvents(devices) {
            const events = [];
            devices.forEach(device => {
                if (!device || !device.latest_event) return;
                const event = device.latest_event;
                if (event.point_latitude && event.point_longitude) {
                    events.push({
                        ...event,
                        imei: device.imei,
                        name: device.name,
                        display_name: device.display_name,
                        color: device.color
                    });
                }
            });
            return events;
        },

        /**
         * Ajuste dynamiquement la fréquence de polling
         */
        adjustPollingRate(hasMovement) {
            const newRate = hasMovement ? this.FAST_POLLING_RATE : this.NORMAL_POLLING_RATE;

            // Gestion du compteur de cycles sans mouvement
            if (hasMovement) {
                this.cyclesWithoutMovement = 0;
            } else {
                this.cyclesWithoutMovement++;
            }

            // Si pas de mouvement depuis un certain temps, revenir au mode normal
            const shouldReturnToNormal = !hasMovement &&
                                         this.cyclesWithoutMovement >= this.FAST_MODE_CYCLES_BEFORE_NORMAL &&
                                         this.currentPollingRate === this.FAST_POLLING_RATE;

            if (newRate !== this.currentPollingRate || shouldReturnToNormal) {
                console.log(`⚡ Changement de fréquence : ${this.currentPollingRate/1000}s → ${newRate/1000}s`);

                // Arrêter l'ancien timer
                if (this.pollingInterval) {
                    clearInterval(this.pollingInterval);
                }

                // Nouvelle fréquence
                this.currentPollingRate = newRate;

                // Redémarrer avec la nouvelle fréquence
                this.pollingInterval = setInterval(() => {
                    this.pollDevices();
                }, newRate);

                // Feedback visuel
                this.showRateChangeNotification(newRate);
            }
        },

        /**
         * Affiche une notification de mouvement en haut de l'écran
         */
        showMovementNotificationTop(movingDevices) {
            const message = movingDevices.map(device => {
                const distanceStr = device.distance < 1000
                    ? `${device.distance.toFixed(0)}m`
                    : `${(device.distance / 1000).toFixed(2)}km`;
                return `🚗 ${device.name} (${distanceStr})`;
            }).join(' | ');

            // Créer le toast en haut
            const toast = document.createElement('div');
            toast.className = 'movement-toast-top';
            toast.innerHTML = `
                <div class="movement-toast-content">
                    <strong>⚡ Mouvement détecté:</strong>
                    <span class="movement-devices">${message}</span>
                </div>
            `;

            // Style CSS inline
            toast.style.position = 'fixed';
            toast.style.top = '20px';
            toast.style.left = '50%';
            toast.style.transform = 'translateX(-50%)';
            toast.style.zIndex = '9999';
            toast.style.padding = '15px 25px';
            toast.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
            toast.style.color = 'white';
            toast.style.borderRadius = '12px';
            toast.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
            toast.style.fontFamily = 'system-ui, sans-serif';
            toast.style.fontSize = '14px';
            toast.style.opacity = '0';
            toast.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
            toast.style.transform = 'translateX(-50%) translateY(-20px)';
            toast.style.maxWidth = '90%';
            toast.style.textAlign = 'center';

            document.body.appendChild(toast);

            // Animation d'apparition
            setTimeout(() => {
                toast.style.opacity = '1';
                toast.style.transform = 'translateX(-50%) translateY(0)';
            }, 50);

            // Supprimer après 8 secondes
            setTimeout(() => {
                toast.style.opacity = '0';
                toast.style.transform = 'translateX(-50%) translateY(-20px)';
                setTimeout(() => toast.remove(), 300);
            }, 8000);
        },

        /**
         * Affiche une notification de mouvement
         */
        showMovementNotification(movingDevices) {
            const message = movingDevices.length === 1
                ? `🚗 ${movingDevices[0].name} en mouvement (${movingDevices[0].distance.toFixed(0)}m)`
                : `🚗 ${movingDevices.length} devices en mouvement`;

            this.movementStatus = {
                active: true,
                message: message,
                count: movingDevices.length,
                timestamp: new Date()
            };

            // Afficher seulement si pas déjà affiché récemment
            if (!this.lastMovementNotification ||
                Date.now() - this.lastMovementNotification > 10000) {
                this.showToast(message);
                this.lastMovementNotification = Date.now();
            }
        },

        /**
         * Affiche une notification de changement de fréquence
         */
        showRateChangeNotification(newRate) {
            const rateText = newRate === this.FAST_POLLING_RATE
                ? '5s (Mode rapide - Mouvement détecté)'
                : '30s (Mode normal)';

            this.showToast(`⚡ Fréquence de mise à jour : ${rateText}`);
        },

        /**
         * Retourne la date de dernière connexion d'un device
         */
        getTimeSinceLastConnection(device) {
            // Essayer latest_event d'abord, puis eventsdata[0]
            const latestEvent = device.latest_event || (device.eventsdata && device.eventsdata[0]);

            if (!latestEvent) {
                return '⚪ Jamais';
            }

            // Essayer différents champs de date
            let dateStr = latestEvent.received_date ||
                         latestEvent.created_at ||
                         latestEvent.updated_at;

            if (!dateStr) {
                return '⚪ Jamais';
            }

            const lastConnection = new Date(dateStr);

            // Si la date est invalide
            if (isNaN(lastConnection.getTime())) {
                return '⚪ Inconnue';
            }

            // Formater la date : JJ/MM/AAAA HH:MM:SS
            const day = String(lastConnection.getDate()).padStart(2, '0');
            const month = String(lastConnection.getMonth() + 1).padStart(2, '0');
            const year = lastConnection.getFullYear();
            const hours = String(lastConnection.getHours()).padStart(2, '0');
            const minutes = String(lastConnection.getMinutes()).padStart(2, '0');
            const seconds = String(lastConnection.getSeconds()).padStart(2, '0');

            return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
        }
    }
});
</script>